import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { inspectionRoutesService } from "../services/inspectionRoutesService";
import styles from "../assets/inspectionRouteShow.module.css";

const InspectionRouteShow = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // State
  const [inspectionRoute, setInspectionRoute] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [searchProperty, setSearchProperty] = useState("");
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [propertySuggestions, setPropertySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Table controls state
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [globalSearch, setGlobalSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    id: true,
    postcode: true,
    status: true,
    action: true
  });

  // Load inspection route data on component mount
  useEffect(() => {
    loadInspectionRoute();
  }, [id]);

  // Load inspection route data
  const loadInspectionRoute = async () => {
    setLoading(true);
    try {
      if (useMockData) {
        // Simulate API call with mock data
        await new Promise((resolve) => setTimeout(resolve, 500));
        const mockData = inspectionRoutesService.getMockInspectionRoutes();
        const route = mockData.find((route) => route.id === parseInt(id));
        
        if (route) {
          setInspectionRoute(route);
                     // Mock properties data
           const mockProperties = [
             {
               id: 1417444501,
               name: "Cranwood",
               postcode: "N10 3JA",
               status: "Ended"
             },
             {
               id: 1417444502,
               name: "Property B", 
               postcode: "SW1A 2BB",
               status: "Active"
             },
             {
               id: 1417444503,
               name: "Property C",
               postcode: "SW1A 3CC", 
               status: "Inactive"
             }
           ];
          setProperties(mockProperties);
          setFilteredProperties(mockProperties);
        } else {
          toast.error("Inspection route not found");
          navigate("/inspection-routes");
        }
      } else {
        try {
          const response = await inspectionRoutesService.getInspectionRoute(id);
          setInspectionRoute(response.data);
          
          // Load properties for this route
          const propertiesResponse = await inspectionRoutesService.getInspectionRouteProperties(id);
          // Ensure properties is always an array
          const propertiesData = Array.isArray(propertiesResponse.data) 
            ? propertiesResponse.data 
            : (Array.isArray(propertiesResponse) ? propertiesResponse : []);
          setProperties(propertiesData);
          setFilteredProperties(propertiesData);
        } catch (apiError) {
          console.error("API call failed:", apiError);
          // Fallback to mock data if API fails
          const mockData = inspectionRoutesService.getMockInspectionRoutes();
          const route = mockData.find((route) => route.id === parseInt(id));
          
          if (route) {
            setInspectionRoute(route);
                         const mockProperties = [
               {
                 id: 1417444501,
                 name: "Cranwood",
                 postcode: "N10 3JA",
                 status: "Ended"
               },
               {
                 id: 1417444502,
                 name: "Property B", 
                 postcode: "SW1A 2BB",
                 status: "Active"
               },
               {
                 id: 1417444503,
                 name: "Property C",
                 postcode: "SW1A 3CC", 
                 status: "Inactive"
               }
             ];
            setProperties(mockProperties);
            setFilteredProperties(mockProperties);
            toast.warning("Using mock data due to API failure");
          } else {
            toast.error("Inspection route not found");
            navigate("/inspection-routes");
          }
        }
      }
    } catch (error) {
      console.error("Error loading inspection route:", error);
      toast.error("Failed to load inspection route");
      navigate("/inspection-routes");
    } finally {
      setLoading(false);
    }
  };

  // Reload data when mock data toggle changes
  useEffect(() => {
    if (!loading) {
      loadInspectionRoute();
    }
  }, [useMockData]);

  // Handle property search
  const handlePropertySearch = async (e) => {
    const searchTerm = e.target.value;
    setSearchProperty(searchTerm);
    
    // Ensure properties is an array
    const propertiesArray = Array.isArray(properties) ? properties : [];
    
    if (searchTerm.trim() === "") {
      setFilteredProperties(propertiesArray);
      setPropertySuggestions([]);
      setShowSuggestions(false);
    } else {
      // Filter existing properties
      const filtered = propertiesArray.filter(property => 
        (property.name && property.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (property.id && property.id.toString().includes(searchTerm)) ||
        (property.postcode && property.postcode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProperties(filtered);
      
      // Get property suggestions from live database
      try {
        if (!useMockData) {
          const suggestions = await inspectionRoutesService.searchProperties(searchTerm);
          // Filter out properties that are already added to this route
          // Ensure properties is an array
          const propertiesArray = Array.isArray(properties) ? properties : [];
          const availableSuggestions = Array.isArray(suggestions) 
            ? suggestions.filter(suggestion => 
                !propertiesArray.some(property => property.id === suggestion.id)
              )
            : [];
          setPropertySuggestions(availableSuggestions);
          setShowSuggestions(availableSuggestions.length > 0);
        }
      } catch (error) {
        console.error("Error getting property suggestions:", error);
        setPropertySuggestions([]);
        setShowSuggestions(false);
      }
    }
  };

  // Handle edit route
  const handleEdit = () => {
    navigate(`/inspection-routes/${id}/edit`);
  };

  // Handle adding property to inspection route
  const handleAddProperty = async (property) => {
    try {
      // Ensure properties is an array
      const propertiesArray = Array.isArray(properties) ? properties : [];
      
      if (useMockData) {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 500));
        const newProperty = {
          id: property.id,
          name: property.name,
          postcode: property.postcode,
          status: property.status || "Active"
        };
        const updatedProperties = [...propertiesArray, newProperty];
        setProperties(updatedProperties);
        setFilteredProperties(updatedProperties);
        setSearchProperty("");
        setPropertySuggestions([]);
        setShowSuggestions(false);
        toast.success("Property added successfully! (mock)");
      } else {
        await inspectionRoutesService.addPropertyToRoute(id, property.id);
        const newProperty = {
          id: property.id,
          name: property.name,
          postcode: property.postcode,
          status: property.status || "Active"
        };
        const updatedProperties = [...propertiesArray, newProperty];
        setProperties(updatedProperties);
        setFilteredProperties(updatedProperties);
        setSearchProperty("");
        setPropertySuggestions([]);
        setShowSuggestions(false);
        toast.success("Property added successfully!");
      }
    } catch (error) {
      console.error("Error adding property:", error);
      toast.error("Failed to add property");
    }
  };

  // Handle property removal
  const handleRemoveProperty = async (propertyId) => {
    try {
      // Ensure properties is an array
      const propertiesArray = Array.isArray(properties) ? properties : [];
      
      if (useMockData) {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 500));
        const updatedProperties = propertiesArray.filter(p => p.id !== propertyId);
        setProperties(updatedProperties);
        setFilteredProperties(updatedProperties);
        toast.success("Property removed successfully! (mock)");
      } else {
        await inspectionRoutesService.removePropertyFromRoute(id, propertyId);
        const updatedProperties = propertiesArray.filter(p => p.id !== propertyId);
        setProperties(updatedProperties);
        setFilteredProperties(updatedProperties);
        toast.success("Property removed successfully!");
      }
    } catch (error) {
      console.error("Error removing property:", error);
      toast.error("Failed to remove property");
    }
  };

  // Handle global search
  const handleGlobalSearch = (e) => {
    const searchTerm = e.target.value;
    setGlobalSearch(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing entries per page
  };

  // Handle column visibility toggle
  const handleColumnVisibilityToggle = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Handle export as CSV
  const handleExportCSV = async () => {
    try {
      if (useMockData) {
        // Simulate CSV export
        toast.success("CSV export started! (mock)");
      } else {
        await inspectionRoutesService.exportCSV({ routeId: id });
        toast.success("CSV export started!");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  // Handle export as PDF
  const handleExportPDF = async () => {
    try {
      if (useMockData) {
        // Simulate PDF export
        toast.success("PDF export started! (mock)");
      } else {
        await inspectionRoutesService.exportPDF({ routeId: id });
        toast.success("PDF export started!");
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  // Calculate pagination
  // Ensure filteredProperties is always an array before calling filter
  const filteredData = Array.isArray(filteredProperties) 
    ? filteredProperties.filter(property => {
        if (!globalSearch) return true;
        const searchLower = globalSearch.toLowerCase();
        return (
          (property.name && property.name.toLowerCase().includes(searchLower)) ||
          (property.id && property.id.toString().includes(globalSearch)) ||
          (property.postcode && property.postcode.toLowerCase().includes(searchLower)) ||
          (property.status && property.status.toLowerCase().includes(searchLower))
        );
      })
    : [];

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading inspection route...</p>
        </div>
      </div>
    );
  }

  if (!inspectionRoute) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Inspection route not found</p>
          <button onClick={() => navigate("/inspection-routes")}>
            Back to Inspection Routes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/inspection-routes">Inspection Routes</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>{inspectionRoute.name}</span>
      </div>

      {/* Mock Data Toggle (for development) */}
      <div className={styles.mockDataToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
        
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          {/* Overview Section */}
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Inspection Route</h3>
            </div>
                         <div className={styles.panelBody}>
               <dl className={styles.definitionList}>
                 <div>
                   <dt>Name:</dt>
                   <dd>
                     <input 
                       type="text" 
                       value={inspectionRoute.name} 
                       readOnly 
                       style={{ cursor: "text" }}
                     />
                   </dd>
                 </div>
                 <div>
                   <dt>Building Manager:</dt>
                   <dd>
                     <input 
                       type="text" 
                       value={inspectionRoute.buildingManagerName || ""} 
                       readOnly 
                       style={{ cursor: "text" }}
                     />
                   </dd>
                 </div>
                 <div>
                   <dt>Number of properties:</dt>
                   <dd>
                     <input 
                       type="text" 
                       value={Array.isArray(properties) ? properties.length.toString() : "0"} 
                       readOnly 
                       style={{ cursor: "text" }}
                     />
                   </dd>
                 </div>
               </dl>
             </div>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <div className={styles.panelTitle}>
                <h3>Properties</h3>
                <button
                  style={{ backgroundColor: "#1B2046" }}
                  onClick={handleEdit}
                  className={styles.btnSecondary}
                >
                  <i className="glyphicon glyphicon-edit"></i>
                  Edit
                </button>
              </div>
            </div>
            <div className={styles.panelBody}>
              {/* Property Search */}
              <div className={styles.propertySearch}>
                <div className={styles.searchDescription}>
                  Find property which you want to add to the inspection route. It will be added after click on suggestion in dropdown.
                </div>
                <div className={styles.row}>
                  <div className={styles.colMd6}>
                    <div className={styles.propertySearchContainer}>
                      <input
                        type="text"
                        className={styles.formControl}
                        placeholder="Type property ID or name (or *)"
                        value={searchProperty}
                        onChange={handlePropertySearch}
                        style={{ border: "1px solid #0a7280" }}
                      />
                      {showSuggestions && propertySuggestions.length > 0 && (
                        <div className={styles.propertySuggestions}>
                          {propertySuggestions.map((property) => (
                            <div
                              key={property.id}
                              className={styles.propertySuggestion}
                              onClick={() => handleAddProperty(property)}
                            >
                              <div className={styles.propertyName}>
                                {property.name}
                              </div>
                              <div className={styles.propertyDetails}>
                                ID: {property.property_id || property.id} | {property.postcode}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

                             {/* Properties Table */}
               {Array.isArray(properties) && properties.length > 0 ? (
                 <div className={styles.tableWrapper}>
                   {/* Table Controls */}
                   <div className={styles.tableControls}>
                     <div className={styles.entriesControl}>
                       <span>Show</span>
                       <select 
                         value={entriesPerPage} 
                         onChange={handleEntriesPerPageChange}
                         className={styles.entriesSelect}
                       >
                         <option value={10}>10</option>
                         <option value={25}>25</option>
                         <option value={30}>30</option>
                         <option value={50}>50</option>
                         <option value={100}>100</option>
                       </select>
                       <span>entries</span>
                     </div>
                     
                     <div className={styles.globalSearch}>
                       <span>Search:</span>
                       <input
                       style={{ width: "400px" }}
                         type="text"
                         value={globalSearch}
                         onChange={handleGlobalSearch}
                         className={styles.searchInput}
                         placeholder="Search properties..."
                       />
                     </div>
                     
                     <div className={styles.exportControls}>
                       <button
                         onClick={handleExportCSV}
                         className={styles.exportBtn}
                       >
                         Export as CSV
                       </button>
                       <button
                         onClick={handleExportPDF}
                         className={styles.exportBtn}
                       >
                         Export as PDF
                       </button>
                       <div className={styles.columnVisibility}>
                         <button
                           onClick={() => setShowColumnVisibility(!showColumnVisibility)}
                           className={styles.exportBtn}
                         >
                           Column visibility
                         </button>
                         {showColumnVisibility && (
                           <div className={styles.visibilityDropdown}>
                             {Object.entries(visibleColumns).map(([column, isVisible]) => (
                               <label key={column} className={styles.visibilityOption}>
                                 <input
                                   type="checkbox"
                                   checked={isVisible}
                                   onChange={() => handleColumnVisibilityToggle(column)}
                                 />
                                 <span className={styles.columnName}>
                                   {column.charAt(0).toUpperCase() + column.slice(1)}
                                 </span>
                               </label>
                             ))}
                           </div>
                         )}
                       </div>
                     </div>
                   </div>

                   {/* Data Table */}
                   <table className={styles.dataTable}>
                     <thead>
                       <tr>
                         {visibleColumns.name && <th>Name</th>}
                         {visibleColumns.id && <th>ID</th>}
                         {visibleColumns.postcode && <th>Postcode</th>}
                         {visibleColumns.status && <th>Status</th>}
                         {visibleColumns.action && <th className={styles.actionHead}>Action</th>}
                       </tr>
                     </thead>
                     <tbody>
                       {paginatedData.map((property) => (
                         <tr key={property.id}>
                           {visibleColumns.name && (
                             <td>
                               <Link to={`/properties/${property.id}`} className={styles.propertyLink}>
                                 {property.name}
                               </Link>
                             </td>
                           )}
                           {visibleColumns.id && <td>{property.id}</td>}
                           {visibleColumns.postcode && <td>{property.postcode}</td>}
                           {visibleColumns.status && (
                             <td>
                               <span className={`${styles.status} ${styles[property.status.toLowerCase()]}`}>
                                 {property.status}
                               </span>
                             </td>
                           )}
                           {visibleColumns.action && (
                             <td className={styles.actionCell}>
                               <button
                                 onClick={() => handleRemoveProperty(property.id)}
                                 className={styles.btnDetach}
                                 title="Detach property from route"
                               >
                                 Detach
                               </button>
                             </td>
                           )}
                         </tr>
                       ))}
                     </tbody>
                   </table>

                   {/* Pagination */}
                   <div className={styles.pagination}>
                     <div className={styles.paginationInfo}>
                       Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
                     </div>
                     <div className={styles.paginationControls}>
                       <button
                         onClick={() => handlePageChange(currentPage - 1)}
                         disabled={currentPage === 1}
                         className={styles.paginationBtn}
                       >
                         Previous
                       </button>
                       {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                         <button
                           key={page}
                           onClick={() => handlePageChange(page)}
                           className={`${styles.paginationBtn} ${currentPage === page ? styles.activePage : ''}`}
                         >
                           {page}
                         </button>
                       ))}
                       <button
                         onClick={() => handlePageChange(currentPage + 1)}
                         disabled={currentPage === totalPages}
                         className={styles.paginationBtn}
                       >
                         Next
                       </button>
                     </div>
                   </div>
                 </div>
               ) : (
                 <div className={styles.noProperties}>
                   No property!
                 </div>
               )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionRouteShow; 